/**
 * CategoryPicker — combines a category select (with "SanMar suggests" +
 * live-used categories + custom "add new") and a subcategory select
 * with the same UX. Emits `{category, subcategory}` to the parent.
 *
 * When `inferred` is provided, an ✨ chip shows the classifier's guess
 * so the admin can tell whether they're accepting or overriding it.
 */
import { useEffect, useMemo, useState } from "react";
import { Sparkles, Plus } from "lucide-react";

export default function CategoryPicker({
  taxonomy,          // {standard_categories, standard_subcategories, live_categories, live_subcategories}
  inferred,          // {category, subcategory} — from /admin/categories/classify
  value,             // {category, subcategory} — current selection
  onChange,          // ({category, subcategory}) => void
  compact = false,
  testidPrefix = "cat",
}) {
  const [customCat, setCustomCat] = useState(false);
  const [customSub, setCustomSub] = useState(false);
  const [newCatText, setNewCatText] = useState("");
  const [newSubText, setNewSubText] = useState("");

  const categories = useMemo(() => {
    if (!taxonomy) return [];
    return Array.from(new Set([
      ...(taxonomy.standard_categories || []),
      ...(taxonomy.live_categories || []),
    ])).sort();
  }, [taxonomy]);
  const subcategories = useMemo(() => {
    if (!taxonomy) return [];
    return Array.from(new Set([
      ...(taxonomy.standard_subcategories || []),
      ...(taxonomy.live_subcategories || []),
    ])).sort();
  }, [taxonomy]);

  // Auto-apply inferred defaults if nothing is set yet
  useEffect(() => {
    if (!value?.category && inferred?.category) {
      onChange?.({ category: inferred.category, subcategory: inferred.subcategory });
    }
  }, [inferred?.category, inferred?.subcategory]);  // eslint-disable-line react-hooks/exhaustive-deps

  const acceptInferred = () => onChange?.({ category: inferred.category, subcategory: inferred.subcategory });

  const setCategory = (c) => onChange?.({ category: c, subcategory: value?.subcategory || "" });
  const setSubcategory = (s) => onChange?.({ category: value?.category || "", subcategory: s });

  return (
    <div className={compact ? "space-y-2" : "space-y-3"} data-testid={`${testidPrefix}-picker`}>
      {inferred && (
        <div className="flex items-center gap-2 flex-wrap font-mono text-[10px] tracking-widest">
          <Sparkles size={11} className="text-purple-300" />
          <span className="text-zinc-400">CLASSIFIER SUGGESTS:</span>
          <span className="border border-purple-800 bg-purple-950/40 text-purple-200 px-1.5 py-0.5">
            {inferred.category} · {inferred.subcategory}
          </span>
          {(value?.category !== inferred.category || value?.subcategory !== inferred.subcategory) && (
            <button
              type="button" onClick={acceptInferred}
              data-testid={`${testidPrefix}-accept-inferred`}
              className="text-purple-200 hover:text-white border border-purple-800 px-2 py-0.5"
            >
              USE SUGGESTION
            </button>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1">CATEGORY</div>
          {!customCat ? (
            <select
              value={value?.category || ""}
              onChange={(e) => {
                if (e.target.value === "__custom__") { setCustomCat(true); return; }
                setCategory(e.target.value);
              }}
              data-testid={`${testidPrefix}-cat-select`}
              className="w-full bg-black border border-[#222] px-2 py-1.5 font-mono text-xs"
            >
              <option value="">— choose —</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c.toUpperCase()}</option>
              ))}
              <option value="__custom__">+ ADD NEW CATEGORY…</option>
            </select>
          ) : (
            <div className="flex items-center gap-1">
              <input
                type="text" value={newCatText}
                onChange={(e) => setNewCatText(e.target.value)}
                placeholder="e.g. bags, socks"
                data-testid={`${testidPrefix}-cat-new-input`}
                className="flex-1 bg-black border border-[#222] px-2 py-1.5 font-mono text-xs"
              />
              <button
                type="button"
                onClick={() => { if (newCatText.trim()) setCategory(newCatText.trim().toLowerCase()); setCustomCat(false); setNewCatText(""); }}
                data-testid={`${testidPrefix}-cat-new-save`}
                className="border border-emerald-800 bg-emerald-950 text-emerald-200 px-2 py-1.5 font-mono text-[10px]"
              >
                <Plus size={11} />
              </button>
              <button
                type="button" onClick={() => { setCustomCat(false); setNewCatText(""); }}
                className="border border-[#333] text-zinc-400 px-2 py-1.5 font-mono text-[10px]"
              >
                ×
              </button>
            </div>
          )}
        </div>

        <div>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1">SUBCATEGORY</div>
          {!customSub ? (
            <select
              value={value?.subcategory || ""}
              onChange={(e) => {
                if (e.target.value === "__custom__") { setCustomSub(true); return; }
                setSubcategory(e.target.value);
              }}
              data-testid={`${testidPrefix}-sub-select`}
              className="w-full bg-black border border-[#222] px-2 py-1.5 font-mono text-xs"
            >
              <option value="">— choose —</option>
              {subcategories.map((s) => (
                <option key={s} value={s}>{s.toUpperCase()}</option>
              ))}
              <option value="__custom__">+ ADD NEW SUBCATEGORY…</option>
            </select>
          ) : (
            <div className="flex items-center gap-1">
              <input
                type="text" value={newSubText}
                onChange={(e) => setNewSubText(e.target.value)}
                placeholder="e.g. maternity, athletic"
                data-testid={`${testidPrefix}-sub-new-input`}
                className="flex-1 bg-black border border-[#222] px-2 py-1.5 font-mono text-xs"
              />
              <button
                type="button"
                onClick={() => { if (newSubText.trim()) setSubcategory(newSubText.trim().toLowerCase()); setCustomSub(false); setNewSubText(""); }}
                className="border border-emerald-800 bg-emerald-950 text-emerald-200 px-2 py-1.5 font-mono text-[10px]"
              >
                <Plus size={11} />
              </button>
              <button
                type="button" onClick={() => { setCustomSub(false); setNewSubText(""); }}
                className="border border-[#333] text-zinc-400 px-2 py-1.5 font-mono text-[10px]"
              >
                ×
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
